import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Select, InputNumber, Table, message, Card } from "antd";
import api from "../services/api";

const { Option } = Select;

const MatchLive = () => {
  const { id } = useParams(); // match_id
  const navigate = useNavigate();

  const [matchTeams, setMatchTeams] = useState([]);
  const [inning, setInning] = useState(null);
  const [battingTeam, setBattingTeam] = useState(null);
  const [bowlingTeam, setBowlingTeam] = useState(null);
  const [batters, setBatters] = useState([]);
  const [bowlers, setBowlers] = useState([]);
  const [striker, setStriker] = useState(null);
  const [nonStriker, setNonStriker] = useState(null);
  const [currentBowler, setCurrentBowler] = useState(null);
  const [runs, setRuns] = useState(0);
  const [balls, setBalls] = useState([]);
  const [over, setOver] = useState(1);
  const [ballCount, setBallCount] = useState(0);
  const [target, setTarget] = useState(null);
  const [inningsCount, setInningsCount] = useState(0);
  const [matchEnded, setMatchEnded] = useState(false);
  const [winner, setWinner] = useState(null);
  const [allInnings, setAllInnings] = useState([]);
  const [matchDetails, setMatchDetails] = useState(null);
  const [showWicketModal, setShowWicketModal] = useState(false);
  const [wicketType, setWicketType] = useState(null);
  const [fielder, setFielder] = useState(null);

useEffect(() => {
  const fetchMatchDetails = async () => {
    try {
      const res = await api.get(`/matches/${id}`);
      setMatchDetails(res.data.data);
      if (res.data.data.status === "completed") {
        setMatchEnded(true);
      }
    } catch (error) {
      console.error("Error fetching match:", error);
      message.error("Failed to load match details");
    }
  };

  fetchMatchDetails();
}, [id]);

  useEffect(() => {
    fetchMatchTeams();
    checkOngoingInning();
  }, [id]);

  const fetchMatchTeams = async () => {
    try {
      const res = await api.get(`/matchteams/${id}`, { params: { match_id: id } });
      setMatchTeams(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPlayers = async (team_id) => {
    try {
      const res = await api.get("/team-players", { params: { team_id } });
      return res.data.data.map((p) => p.Player); // { player_id, name }
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const fetchBalls = async (inningId) => {
    try {
      const res = await api.get(`/balls/inning/${inningId}`);
      return res.data.data || [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const computeInningState = (ballsData, batPlayers) => {
    let totalRuns = 0;
    let overNum = 1;
    let ballNum = 0;
    let remainingBatters = [...batPlayers];
    let lastStriker = null;
    let lastNonStriker = null;
    let lastBowler = null;

    ballsData.forEach((b) => {
      const runsThisBall = b.runs + (b.extras || 0);
      totalRuns += runsThisBall;

      if (b.extras !== "wide" && b.extras !== "no-ball") {
        ballNum = b.ball_number;
        if (ballNum === 6) {
          overNum = b.over + 1;
          ballNum = 0;
        } else {
          overNum = b.over;
        }
      }

      if (b.wicket) {
        remainingBatters = remainingBatters.filter(
          (p) => p.player_id !== b.batsman_id
        );
      }

      lastStriker = b.batsman_id;
      lastNonStriker = b.non_striker_id;
      lastBowler = b.bowler_id;
    });

    setRuns(totalRuns);
    setOver(overNum);
    setBallCount(ballNum);
    setStriker(lastStriker);
    setNonStriker(lastNonStriker);
    setCurrentBowler(lastBowler);
    setBatters(remainingBatters);
  };

  const checkOngoingInning = async () => {
    try {
      const res = await api.get(`/innings/match/${id}`);
      const inningsData = res.data.data || [];
      setAllInnings(inningsData);
      setInningsCount(inningsData.length);

      if (inningsData.length > 0) {
        const ongoing = inningsData[inningsData.length - 1];
        setInning(ongoing);
        setBattingTeam(ongoing.batting_team_id);
        setBowlingTeam(ongoing.bowling_team_id);

        const batPlayers = await fetchPlayers(ongoing.batting_team_id);
        const bowlPlayers = await fetchPlayers(ongoing.bowling_team_id);
        setBatters(batPlayers);
        setBowlers(bowlPlayers);

        const ballsData = await fetchBalls(ongoing.inning_id);
        setBalls(ballsData);
        computeInningState(ballsData, batPlayers);

        const inningsWithBalls = await Promise.all(
          inningsData.map(async (inn) => {
            const balls = await fetchBalls(inn.inning_id);
            return { ...inn, balls };
          })
        );
        setAllInnings(inningsWithBalls);

        // Set target if 2nd inning ongoing
        if (inningsData.length === 2) {
          const firstInningBalls = inningsWithBalls[0].balls;
          const firstInningTotal = firstInningBalls.reduce(
            (acc, b) => acc + b.runs + (b.extras || 0),
            0
          );
          setTarget(firstInningTotal + 1);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleStartInning = async () => {
    if (!battingTeam || !bowlingTeam) {
      message.warning("Select both batting and bowling teams!");
      return;
    }

    try {
      const res = await api.post("/innings", {
        match_id: id,
        batting_team_id: battingTeam,
        bowling_team_id: bowlingTeam,
      });
      setInning(res.data.data);
      message.success("Inning started!");

      const batPlayers = await fetchPlayers(battingTeam);
      const bowlPlayers = await fetchPlayers(bowlingTeam);
      setBatters(batPlayers);
      setBowlers(bowlPlayers);

      if (batPlayers.length > 1) {
        setStriker(batPlayers[0].player_id);
        setNonStriker(batPlayers[1].player_id);
      }
      setInningsCount((prev) => prev + 1);
    } catch (err) {
      console.error(err);
      message.error("Failed to start inning");
    }
  };

  const handleWicketClick = () => {
  if (!striker) return;
  setShowWicketModal(true);
};

const confirmWicket = () => {
  handleBall(0, null, true); // mark ball as wicket

  // Include wicket details in backend
  const lastBall = balls[balls.length - 1];
  api.put(`/balls/${lastBall.ball_id}`, {
    wicket_type: wicketType,
    fielder_id: fielder,
  }).catch(err => console.error(err));

  // Reset wicket modal
  setShowWicketModal(false);
  setWicketType(null);
  setFielder(null);
};

  const handleBall = async (run = 0, extraType = null, wicket = false) => {
    if (!inning || !striker || !nonStriker || !currentBowler || matchEnded) {
      message.warning("Cannot record ball now");
      return;
    }

    let totalRun = run;
    if (extraType === "wide" || extraType === "no-ball") totalRun += 1;

    const ballPayload = {
      match_id: id,
      inning_id: inning.inning_id,
      over,
      ball_number: ballCount + 1,
      batsman_id: striker,
      non_striker_id: nonStriker,
      bowler_id: currentBowler,
      runs: run,
      extras: extraType ? 1 : 0,
      wicket,
    };

    try {
      const res = await api.post("/balls", ballPayload);
      setBalls((prev) => [...prev, res.data.data]);
      setRuns((prev) => prev + totalRun);

      if (!wicket && !extraType && run % 2 === 1) {
        const temp = striker;
        setStriker(nonStriker);
        setNonStriker(temp);
      }

      if (extraType !== "wide" && extraType !== "no-ball") {
        if (ballCount + 1 === 6) {
          setOver((prev) => prev + 1);
          setBallCount(0);
          const temp = striker;
          setStriker(nonStriker);
          setNonStriker(temp);
          message.success(`Over ${over} completed`);
        } else {
          setBallCount(ballCount + 1);
        }
      }

      if (wicket) {
        const remaining = batters.filter((b) => b.player_id !== striker);
        setBatters(remaining);

        if (remaining.length > 0) {
          setStriker(remaining[0].player_id);
          setNonStriker(remaining.length > 1 ? remaining[1].player_id : null);
        } else {
          setStriker(null);
          setNonStriker(null);
          message.success("All batters out, inning ended!");

          // ✅ Update inning summary
          updateInningSummary(inning.inning_id, [...balls, { ...ballPayload, wicket: true }]);
        }
      }

      if (target && runs + totalRun >= target) {
        setMatchEnded(true);
        setWinner(battingTeam);
        message.success("Target achieved! Match ended!");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to record ball");
    }
  };

  const handleWicket = () => {
    message.info(`Batter ${striker} is out!`);
    handleBall(0, null, true);
  };

  const updateInningSummary = async (inningId, ballsData) => {
  try {
    const totalRuns = ballsData.reduce((acc, b) => acc + b.runs + (b.extras || 0), 0);
    const totalWickets = ballsData.filter(b => b.wicket).length;
    const lastBall = ballsData[ballsData.length - 1];
    const totalOvers = lastBall ? lastBall.over + (lastBall.ball_number / 6) : 0;

    await api.put(`/innings/${inningId}`, {
      runs: totalRuns,
      wickets: totalWickets,
      overs: totalOvers.toFixed(1), // optional: show as decimal overs
    });

    console.log(`Inning ${inningId} summary updated`);
  } catch (err) {
    console.error("Failed to update inning summary", err);
  }
};

const handleNextInning = async () => {
  if (!inning || matchEnded) return;
  if (inningsCount >= 2) {
    setMatchEnded(true);
    message.info("Match has ended!");
    return;
  }

  try {
    // ✅ Update current inning summary before starting next
    const totalRuns = balls.reduce((acc, b) => acc + b.runs + (b.extras || 0), 0);
    const totalWickets = balls.filter(b => b.wicket).length;
    const lastBall = balls[balls.length - 1];
    const totalOvers = lastBall ? lastBall.over + (lastBall.ball_number / 6) : 0;

    await api.put(`/innings/${inning.inning_id}`, {
      runs: totalRuns,
      wickets: totalWickets,
      overs: totalOvers.toFixed(1),
    });

    // Swap teams for next inning
    const newBattingTeam = bowlingTeam;
    const newBowlingTeam = battingTeam;

    const res = await api.post("/innings", {
      match_id: id,
      batting_team_id: newBattingTeam,
      bowling_team_id: newBowlingTeam,
    });

    const newInning = res.data.data;
    setInning(newInning);
    setTarget(totalRuns + 1); // target = first inning runs + 1
    setBattingTeam(newBattingTeam);
    setBowlingTeam(newBowlingTeam);

    const batPlayers = await fetchPlayers(newBattingTeam);
    const bowlPlayers = await fetchPlayers(newBowlingTeam);
    setBatters(batPlayers);
    setBowlers(bowlPlayers);

    if (batPlayers.length > 1) {
      setStriker(batPlayers[0].player_id);
      setNonStriker(batPlayers[1].player_id);
    }

    // Reset inning state
    setRuns(0);
    setOver(1);
    setBallCount(0);
    setBalls([]);
    setInningsCount((prev) => prev + 1);

    message.success(`Next Inning started! Target: ${totalRuns + 1}`);
  } catch (err) {
    console.error(err);
    message.error("Failed to start next inning");
  }
};


const handleEndMatch = async () => {
  try {
    if (!allInnings.length) return;

    // 1️⃣ Update summary for each inning
    await Promise.all(
      allInnings.map(async (inn) => {
        const ballsData = inn.balls || [];
        const totalRuns = ballsData.reduce(
          (acc, b) => acc + b.runs + (b.extras || 0),
          0
        );
        const totalWickets = ballsData.filter(b => b.wicket).length;
        const lastBall = ballsData[ballsData.length - 1];
        const totalOvers = lastBall
          ? lastBall.over + lastBall.ball_number / 6
          : 0;

        await api.put(`/innings/${inn.inning_id}`, {
          runs: totalRuns,
          wickets: totalWickets,
          overs: totalOvers.toFixed(1),
        });
      })
    );

    // 2️⃣ Determine winner
    let winnerTeam = null;
    if (allInnings.length === 2 && target) {
      // 2nd inning exists with target
      const secondInningRuns = runs; // already tracked in state
      winnerTeam = secondInningRuns >= target ? battingTeam : bowlingTeam;
    } else if (allInnings.length === 1) {
      // Only 1 inning played
      winnerTeam = allInnings[0].runs > 0 ? allInnings[0].batting_team_id : null;
    }

    setWinner(winnerTeam);
    setMatchEnded(true);

    // 3️⃣ Update match status in backend
    await api.put(`/matches/${id}`, { status: "completed" });

    message.success(
      `Match ended! ${winnerTeam ? `Winner: Team ${winnerTeam}` : "Match Draw!"}`
    );

    // 4️⃣ Redirect after short delay
    setTimeout(() => navigate("/dashboard/matches/"), 5000);
  } catch (err) {
    console.error("Failed to end match", err);
    message.error("Failed to end match");
  }
};  


  const ballColumns = [
    { title: "Over", dataIndex: "over", key: "over" },
    { title: "Ball", dataIndex: "ball_number", key: "ball_number" },
    { title: "Runs", dataIndex: "runs", key: "runs" },
    { title: "Extras", dataIndex: "extras", key: "extras" },
    {
      title: "Batsman",
      key: "batsman",
      render: (_, record) => record.batsman?.name || record.batsman_id,
    },
    {
      title: "Non-Striker",
      key: "nonStriker",
      render: (_, record) => record.non_striker?.name || record.non_striker_id,
    },
    {
      title: "Bowler",
      key: "bowler",
      render: (_, record) => record.bowler?.name || record.bowler_id,
    },
    {
      title: "Wicket",
      dataIndex: "wicket",
      key: "wicket",
      render: (w) => (w ? "Yes" : "No"),
    },
  ];

  return (
    <div>
      <h2>Live Match Control</h2>

      {!inning && !matchEnded && (
        <Card title="Start Inning" style={{ marginBottom: 20 }}>
          <Select
            placeholder="Select Batting Team"
            onChange={setBattingTeam}
            style={{ width: 200, marginRight: 10 }}
            disabled={matchEnded}
          >
            {matchTeams.map((mt) => (
              <Option key={mt.team_id} value={mt.team_id}>
                {mt.Team?.name}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Select Bowling Team"
            onChange={setBowlingTeam}
            style={{ width: 200, marginRight: 10 }}
            disabled={matchEnded}
          >
            {matchTeams.map((mt) => (
              <Option key={mt.team_id} value={mt.team_id}>
                {mt.Team?.name}
              </Option>
            ))}
          </Select>

          <Button type="primary" onClick={handleStartInning} disabled={matchEnded}>
            Start Inning
          </Button>
        </Card>
      )}

      {inning && !matchEnded && (
        <Card title="Player Selection" style={{ marginBottom: 20 }}>
          <Select
            placeholder="Select Striker"
            onChange={setStriker}
            value={striker}
            style={{ width: 200, marginRight: 10 }}
            disabled={matchEnded}
          >
            {batters.map((p) => (
              <Option key={p.player_id} value={p.player_id}>
                {p.name}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Select Non-Striker"
            onChange={setNonStriker}
            value={nonStriker}
            style={{ width: 200, marginRight: 10 }}
            disabled={matchEnded}
          >
            {batters.map((p) => (
              <Option
                key={p.player_id}
                value={p.player_id}
                disabled={p.player_id === striker}
              >
                {p.name}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Select Bowler"
            onChange={setCurrentBowler}
            value={currentBowler}
            style={{ width: 200, marginRight: 10 }}
            disabled={matchEnded}
          >
            {bowlers.map((p) => (
              <Option key={p.player_id} value={p.player_id}>
                {p.name}
              </Option>
            ))}
          </Select>
        </Card>
      )}

        {/* ✅ Hide Add Ball options if match ended */}
   {/* ✅ Add Ball section — completely hidden if match ended */}
{inning &&
  striker &&
  nonStriker &&
  currentBowler &&
  !matchEnded &&
  matchDetails?.status !== "completed" && (
   <Card
  title={`Over ${over - 1}.${ballCount} | Score: ${runs}/${batters.length < 11 ? 11 - batters.length : 0} `}
  style={{ marginBottom: 20 }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10,
    }}
  >
    <div>
      <strong>Runs:</strong>{" "}
      {[0, 1, 2, 3, 4, 5, 6].map((r) => (
        <Button
          key={r}
          onClick={() => handleBall(r)}
          style={{
            marginRight: 6,
            background: r === 4 ? "#1890ff" : r === 6 ? "#52c41a" : "",
            color: r === 4 || r === 6 ? "white" : "",
          }}
        >
          {r}
        </Button>
      ))}
    </div>

    <Button onClick={() => handleBall(0, "wide")} style={{ background: "#fadb14" }}>
      Wide
    </Button>

    <Button
      onClick={() => handleBall(0, "no-ball")}
      style={{ background: "#fa8c16", color: "white" }}
    >
      No Ball
    </Button>

    <Button
      onClick={() => setShowWicketModal(true)}
      style={{ background: "red", color: "white" }}
    >
      Wicket
    </Button>
  </div>
</Card>

  )}

{matchEnded && (
  <Card style={{ marginBottom: 20, background: "#f6f6f6" }}>
    <p>Match has ended — no more balls can be added.</p>
  </Card>
)}

      {/* ✅ Show Next Inning or End Match when only one batter left or all out */}
{inning && !matchEnded && (
  <>
    {batters.length <= 1 && (
      <>
        {inningsCount < 2 ? (
          <Button
            type="primary"
            onClick={handleNextInning}
            style={{ marginBottom: 20 }}
          >
            Next Inning
          </Button>
        ) : (
          <Button
            type="danger"
            onClick={handleEndMatch}
            style={{ marginBottom: 20 }}
          >
            End Match
          </Button>
        )}
      </>
    )}
  </>
)}

{showWicketModal && (
  <Card style={{ marginBottom: 20 }}>
    <h4>Wicket Details</h4>
    <Select
      placeholder="Select Wicket Type"
      onChange={setWicketType}
      value={wicketType}
      style={{ width: 200, marginRight: 10 }}
    >
      <Option value="bowled">Bowled</Option>
      <Option value="caught">Caught</Option>
      <Option value="lbw">LBW</Option>
      <Option value="run-out">Run Out</Option>
      <Option value="stumped">Stumped</Option>
      <Option value="hit-wicket">Hit Wicket</Option>
    </Select>

    {["caught", "run-out", "stumped"].includes(wicketType) && (
      <Select
        placeholder="Select Fielder"
        onChange={setFielder}
        value={fielder}
        style={{ width: 200, marginRight: 10 }}
      >
        {bowlers.concat(batters).map(p => (
          <Option key={p.player_id} value={p.player_id}>{p.name}</Option>
        ))}
      </Select>
    )}

    <Button type="primary" onClick={confirmWicket} disabled={!wicketType}>
      Confirm Wicket
    </Button>
  </Card>
)}


      {/* ✅ Show Both Innings */}
      {allInnings.length > 0 && (
        <div>
          {allInnings.map((inn, idx) => (
            <Card
              key={inn.inning_id}
              title={`Inning ${idx + 1} - Team ${inn.batting_team_id} ${
                target && idx === 1 ? `(Target: ${target})` : ""
              }`}
              style={{ marginBottom: 20 }}
            >
              <Table
                columns={ballColumns}
                dataSource={inn.balls || []}
                rowKey={(record) => record.ball_id || record.id}
              />
            </Card>
          ))}
        </div>
      )}

      {matchEnded && (
        <Card title="Match Ended" style={{ marginTop: 20 }}>
          <p>
            Match has ended. {winner ? `Winner: Team ${winner}` : "Match Draw!"}
          </p>
        </Card>
      )}
    </div>
  );
};

export default MatchLive;
